import * as DomBuilder from "./dom-builder.js";
import * as Utils from "./utils.js";
import * as CsvWriter from "../csv-writer.js";
import DialogDetails from "./DialogDetails.js";
import DialogStats from "./DialogStats.js";
import Filters from "../Filters";

/**
 *
 * @version 2026-09-03
 * @author Patrik Harag
 */
export default class ComponentAnalysisTableCategories {

    /** @type {Controller} */
    #controller;
    /** @type {Map<string,Transaction[]>} */
    #groupedTransactions;
    /** @type {Filter} */
    #selectedFilter;
    /** @type {Map<string,Filter>} */
    #allFilters;

    #tableBuilder = DomBuilder.bootstrapTableBuilder();

    /**
     *
     * @param controller {Controller}
     * @param groupedTransactions {Map<string,Transaction[]>}
     * @param allFilters {Map<string,Filter>}
     * @param selectedFilter {Filter}
     */
    constructor(controller, groupedTransactions, allFilters, selectedFilter) {
        this.#controller = controller;
        this.#groupedTransactions = groupedTransactions;
        this.#allFilters = allFilters;
        this.#selectedFilter = selectedFilter;
    }

    createNode() {
        this.#build(this.#groupedTransactions, this.#allFilters, this.#selectedFilter);
        return this.#tableBuilder.createNode();
    }

    /**
     *
     * @return {string[][]}
     */
    toCsv() {
        let { rowData, categoriesSums } = this.#prepare(this.#groupedTransactions, this.#allFilters);
        let categoryKeys = [];
        Filters.groupByFilters([], this.#allFilters, "Others").forEach((value, key) => {
            if (categoriesSums.has(key)) {
                categoryKeys.push({ key: key, name: value.filter.getName() });
            }
        });

        let header = ['Group'];
        categoryKeys.forEach(c => header.push(c.name));
        header.push('Result', 'Cumulative');
        let rows = [header];

        let sum = 0;
        rowData.forEach(data => {
            let groupSum = 0;
            let row = [data.key];
            data.categories.forEach((value, key) => {
                if (!categoriesSums.has(key)) {
                    return;
                }
                let categorySum = 0;
                value.transactions.forEach(transaction => {
                    categorySum += transaction.getValue();
                });
                groupSum += categorySum;
                row.push(CsvWriter.formatNumber(categorySum));
            });
            sum += groupSum;
            row.push(CsvWriter.formatNumber(groupSum), CsvWriter.formatNumber(sum));
            rows.push(row);
        });

        let footer = [''];
        categoryKeys.forEach(c => footer.push(CsvWriter.formatNumber(categoriesSums.get(c.key))));
        footer.push('', '');
        rows.push(footer);
        return rows;
    }

    #build(groupedTransactions, filters, filter) {
        let { rowData, categoriesSums, categoriesTransactions } = this.#prepare(groupedTransactions, filters);

        // render table rows
        let sum = 0;
        let cumulativeTransactions = [];
        rowData.forEach(data => {
            let groupSum = 0;

            let row = DomBuilder.element('tr');
            row.append(DomBuilder.element('td', { class: 'group-key-cell' }, DomBuilder.span(data.key)));

            data.categories.forEach((value, key) => {
                if (!categoriesSums.has(key)) {
                    // skip this one
                    return;
                }

                let categorySum = 0;
                value.transactions.forEach(transaction => {
                    categorySum += transaction.getValue();
                });
                groupSum += categorySum;

                row.append(DomBuilder.element('td', { class: `value-cell ${categorySum < 0 ? 'negative' : 'positive'}` },
                        Utils.createFxValue(categorySum, value.transactions)));
            });

            sum += groupSum;
            cumulativeTransactions = cumulativeTransactions.concat(data.transactions);

            row.append(DomBuilder.element('td', { class: `value-cell result ${groupSum < 0 ? 'negative' : 'positive'}` },
                    Utils.createFxValue(groupSum, data.transactions)));
            row.append(DomBuilder.element('td', { class: `value-cell result ${sum < 0 ? 'negative' : 'positive'}` },
                    Utils.createFxValue(sum, cumulativeTransactions)));

            // details
            let path = filter.hasFilterFunc() ? filter.getName() + ' / ' : '';
            row.append(DomBuilder.element('td', { class: 'options-cell' }, [
                DomBuilder.link('', { class: 'fa fa-eye' }, () => {
                    let dialog = new DialogDetails(this.#controller, path + data.key, data.transactions, filter, filters);
                    dialog.show();
                }),
                DomBuilder.link('', { class: 'fa fa-th' }, () => {
                    let dialog = new DialogStats(this.#controller, path + data.key, data.transactions, filters);
                    dialog.show();
                })
            ]));

            this.#tableBuilder.addRowBefore(row);
        });

        // render table header
        let headerRow = this.#createTableRowWithLabels(filters, categoriesSums);
        this.#tableBuilder.addRowBefore(headerRow);

        // render table footer
        if (groupedTransactions.size > 10) {
            let footerRow = this.#createTableRowWithLabels(filters, categoriesSums);
            this.#tableBuilder.addRow(footerRow);
        }
        let footerRow = this.#createTableRowWithSums(filters, categoriesSums, categoriesTransactions);
        this.#tableBuilder.addRow(footerRow);
    }

    /**
     *
     * @param groupedTransactions {Map<string,Transaction[]>}
     * @param filters {Map<string,Filter>}
     * @return {{rowData: {categories: Map, key: string, transactions: Transaction[]}[], categoriesSums: Map<string,number>, categoriesTransactions: Map<string,Transaction[]>}}
     */
    #prepare(groupedTransactions, filters) {
        let rowData = Array.from(groupedTransactions.keys()).sort().map(key => {
            let transactions = groupedTransactions.get(key);
            let categories = Filters.groupByFilters(transactions, filters, "Others");
            return {
                categories: categories,
                key: key,
                transactions: transactions
            };
        });

        let categoriesSums = new Map();
        let categoriesTransactions = new Map();
        rowData.forEach(data => {
            data.categories.forEach((value, key) => {
                if (value.transactions.length > 0) {
                    let s = value.transactions.reduce((sum, t) => sum + t.getValue(), 0);
                    let old = categoriesSums.get(key);
                    categoriesSums.set(key, (old !== undefined) ? old + s : s);
                    let collected = categoriesTransactions.get(key);
                    if (collected === undefined) {
                        categoriesTransactions.set(key, value.transactions.slice());
                    } else {
                        collected.push(...value.transactions);
                    }
                }
            });
        });
        return { rowData, categoriesSums, categoriesTransactions };
    }

    #createTableRowWithLabels(filters, categoriesSums) {
        let headerRow = DomBuilder.element('tr');
        headerRow.append(DomBuilder.element('td'));
        Filters.groupByFilters([], filters, "Others").forEach((value, key) => {
            if (categoriesSums.has(key)) {
                let label = (!value.others) ? Utils.createFilterLabel(value.filter) : value.filter.getName();
                headerRow.append(DomBuilder.element('td', { class: 'label-cell' }, label));
            }
        });
        headerRow.append(DomBuilder.element('td'));
        headerRow.append(DomBuilder.element('td'));
        headerRow.append(DomBuilder.element('td'));
        return headerRow;
    }

    #createTableRowWithSums(filters, categoriesSums, categoriesTransactions) {
        let footerRow = DomBuilder.element('tr');
        footerRow.append(DomBuilder.element('td'));
        Filters.groupByFilters([], filters, "Others").forEach((value, key) => {
            let v = categoriesSums.get(key);
            if (v !== undefined) {
                footerRow.append(DomBuilder.element('td', {class: `value-cell result ${v < 0 ? 'negative' : 'positive'}`},
                    Utils.createFxValue(v, categoriesTransactions.get(key))));
            }
        });
        footerRow.append(DomBuilder.element('td'));
        footerRow.append(DomBuilder.element('td'));
        footerRow.append(DomBuilder.element('td'));
        return footerRow;
    }
}
